import moment, { Moment } from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiBadge, EuiButton, EuiFlexGroup, EuiFlexItem, EuiLink, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiText, EuiTitle, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { AIPlanStatus } from 'api/interfaces/AIPlan'
import { useGetAIPlanBlueprintsQuery } from 'api/rtkQueryApi/platform/aiPlanBlueprintsApi'
import { useDeleteAIPlanMutation, useGetAIPlansQuery, useUnhideAIPlanMutation } from 'api/rtkQueryApi/platform/aiPlansApi'
import { RootState } from 'app/rootReducer'
import { AcEuiInMemoryTableWithSearch } from 'components/Basic/AcEuiInMemoryTableWithSearch'
import { reset } from 'features/planner/CreateAIPlanSlice'
import AgencyV2ModalOld from 'features/planner/old-pages/components/AgencyV2ModalOld'

import history from '../../services/HistoryService'

interface AIPlanItem {
  id: string
  name: string
  blueprintName: string
  created: Moment
  status: AIPlanStatus
  isHidden: boolean
}

const getStatusText = (status: AIPlanStatus) => {
  switch (status) {
    case AIPlanStatus.Processing:
      return 'Processing'
    case AIPlanStatus.Ready:
      return 'Ready'
    case AIPlanStatus.CopiedToDsp:
      return 'Copied to DSP'
    case AIPlanStatus.Deleted:
      return 'Deleted'
    case AIPlanStatus.Disapproved:
      return 'Disapproved'
  }
}

const getStatusBadgeColor = (status: AIPlanStatus) => {
  switch (status) {
    case AIPlanStatus.Processing:
      return 'warning'
    case AIPlanStatus.Ready:
    case AIPlanStatus.CopiedToDsp:
      return 'success'
    case AIPlanStatus.Deleted:
    case AIPlanStatus.Disapproved:
      return 'danger'
  }
}

const StatusBadge = (status: AIPlanStatus) => {
  return <EuiBadge color={getStatusBadgeColor(status)}>{getStatusText(status)}</EuiBadge>
}

export const AIPlansPage: React.FC = () => {
  const dispatch = useDispatch()
  const { currentOrganization, currentAccount, isStaffUser, showInternalView } = useSelector((state: RootState) => state.app)
  const { data: plans, isFetching: isFetchingPlans } = useGetAIPlansQuery({ organizationId: currentOrganization?.id ?? '', showHidden: isStaffUser && showInternalView })
  const [accountPlans, setAccountPlans] = useState<AIPlanItem[]>([])
  const { data: blueprints } = useGetAIPlanBlueprintsQuery({ organizationId: currentOrganization?.id ?? '', showHidden: isStaffUser && showInternalView })
  const [deleteAIPlan, deleteAIPlanResult] = useDeleteAIPlanMutation()

  const [processingModalOpen, setProcessingModalOpen] = useState(false)
  const [selectedPlanForDelete, setSelectedPlanForDelete] = useState<AIPlanItem | undefined>()
  const [selectedPlanForUnhide, setSelectedPlanForUnhide] = useState<AIPlanItem | undefined>()
  const [unhidePlan, unhidePlanResult] = useUnhideAIPlanMutation()

  const blueprintNameMap = useMemo(() => {
    const map = new Map<string, string>()
    blueprints?.forEach(b => map.set(b.id, b.name))
    return map
  }, [blueprints])

  useEffect(() => {
    if (isFetchingPlans || !plans || !currentAccount || !blueprintNameMap) {
      return
    }

    const accountSpecificPlans = plans.filter(plan => plan.accountId === currentAccount?.id)
    const withBluePrints = accountSpecificPlans.map(plan => ({
      id: plan.id,
      name: plan.name,
      blueprintName: blueprintNameMap.get(plan.blueprintId) ?? '',
      created: moment(plan.created),
      status: plan.status,
      isHidden: plan.isHidden ?? false
    }))

    if (withBluePrints.length > 0) {
      setAccountPlans(withBluePrints)
    } else {
      dispatch(reset())
      history.push('/ai/plans/welcome')
    }
  }, [blueprints, currentAccount, dispatch, blueprintNameMap, plans, isFetchingPlans])

  const onPlanViewClick = (item: AIPlanItem) => {
    if (!isStaffUser && item.status === AIPlanStatus.Processing) {
      setProcessingModalOpen(true)
      return
    }

    history.push(`/ai/plans/${item.id}`)
  }

  const columns: EuiBasicTableColumn<AIPlanItem>[] = [
    {
      name: 'Plan Name',
      sortable: (item: AIPlanItem) => item.name,
      render: (item: AIPlanItem) => {
        return (
          <React.Fragment>
            <EuiLink onClick={() => onPlanViewClick(item)} color='success'>
              {item.name}
            </EuiLink>
            {item.isHidden && isStaffUser && (
              <>
                {' '}
                <EuiBadge color='accent' onClick={() => setSelectedPlanForUnhide(item)} onClickAriaLabel='Make visible'>
                  Hidden
                </EuiBadge>
              </>
            )}
          </React.Fragment>
        )
      }
    },
    {
      name: 'Date Created',
      field: 'created',
      render: (created: Moment) => {
        return moment(created).format('MM/DD/YYYY')
      },
      sortable: true
    },
    {
      name: 'Status',
      field: 'status',
      sortable: true,
      render: (status: AIPlanStatus) => {
        return StatusBadge(status)
      }
    },
    {
      name: 'Associated Blueprint',
      field: 'blueprintName',
      sortable: true
    },
    {
      name: 'Actions',
      actions: [
        {
          name: 'Delete',
          description: 'Delete this AI plan',
          icon: 'trash',
          type: 'icon',
          color: 'danger',
          onClick: (item: AIPlanItem) => {
            setSelectedPlanForDelete(item)
          },
          enabled: (item: AIPlanItem) => item.status === AIPlanStatus.Ready,
          available: () => !deleteAIPlanResult.isLoading
        }
      ]
    }
  ]

  const sorting = {
    allowNeutralSort: true,
    enableAllColumns: true,
    sort: {
      field: 'created',
      direction: SortDirection.DESC
    }
  }

  const onDeletePlanClick = async () => {
    if (!currentAccount || !currentOrganization || !selectedPlanForDelete) {
      return
    }

    await deleteAIPlan({ organizationId: currentOrganization.id, accountId: currentAccount.id, aiPlanId: selectedPlanForDelete.id })

    setSelectedPlanForDelete(undefined)
  }

  const onCreateClick = () => {
    history.push('/ai/plans/create')
  }

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>AI Plans: {currentAccount?.name}</h1>
            </EuiTitle>
            <EuiText>Use AI Plans to generate strategically targeted campaigns from your blueprints.</EuiText>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false} className='eui-xScroll'>
          <AcEuiInMemoryTableWithSearch
            searchFields={['name']}
            message={isFetchingPlans ? 'Loading AI Plans...' : 'No AI Plans Found'}
            search={{
              toolsRight: [
                <EuiButton fill size={'s'} onClick={onCreateClick}>
                  Create Plan
                </EuiButton>
              ]
            }}
            loading={isFetchingPlans}
            items={isFetchingPlans ? [] : accountPlans}
            columns={columns}
            sorting={sorting}
            compressed
          />
        </EuiPanel>
      </EuiPageBody>
      {processingModalOpen && (
        <AgencyV2ModalOld isLoading={false} title='AI Plan' onClose={() => setProcessingModalOpen(false)} confirmButtonText='OK' onConfirmButtonClick={() => setProcessingModalOpen(false)} bodyHeight='11em'>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>AdCritter AI is currently building your plan with strategically targeted campaigns.</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>Due to strong demand, this can take several hours. We recommend allowing 24 hours just to be safe.</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </AgencyV2ModalOld>
      )}
      {selectedPlanForDelete && (
        <AgencyV2ModalOld isLoading={deleteAIPlanResult.isLoading} title='Delete Plan' confirmButtonText='Delete' onConfirmButtonClick={onDeletePlanClick} onClose={() => setSelectedPlanForDelete(undefined)} showCancelButton>
          <EuiText>Are you sure you want to delete this AI plan?</EuiText>
        </AgencyV2ModalOld>
      )}
      {selectedPlanForUnhide && (
        <AgencyV2ModalOld
          isLoading={unhidePlanResult.isLoading}
          title='Make Plan Visible'
          confirmButtonText='Make Visible'
          onConfirmButtonClick={async () => {
            if (currentOrganization && currentAccount && selectedPlanForUnhide) {
              await unhidePlan({ organizationId: currentOrganization.id, accountId: currentAccount.id, aiPlanId: selectedPlanForUnhide.id })
              setSelectedPlanForUnhide(undefined)
            }
          }}
          onClose={() => setSelectedPlanForUnhide(undefined)}
          showCancelButton>
          <EuiText>This plan is currently hidden from non-staff users. Would you like to make it visible?</EuiText>
        </AgencyV2ModalOld>
      )}
    </EuiPage>
  )
}
